import bearLogo from 'assets/tokenImg/bear.png';
import kavaLogo from 'assets/tokenImg/kava.svg';
import lionLogo from 'assets/tokenImg/lion.png';
import tigerLogo from 'assets/tokenImg/tiger.png';
import { ConnectKitButton } from 'connectkit';
import { useEffect, useState } from 'react';
import { BsDiscord } from 'react-icons/bs';
import { FaBars, FaTelegramPlane, FaTimes, FaTwitter } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import { formatUnits } from 'viem';
import { useAccount, useChainId, useReadContract, useSwitchChain } from 'wagmi';
import './headerModern.css';

const KAVA_CHAIN_ID = 2222;
const ERC20_ABI = [
  {
    "constant": true,
    "inputs": [{"name": "_owner", "type": "address"}],
    "name": "balanceOf",
    "outputs": [{"name": "balance", "type": "uint256"}],
    "type": "function"
  }
];

const TOKEN_ADDRESSES = {
  LION: '0x990e157fC8a492c28F5B50022F000183131b9026',
  TIGER: '0x471F79616569343e8e84a66F342B7B433b958154',
  BEAR: '0x38481Fdc1aF61E6E72E0Ff46F069315A59779C65',
};

export default function ModernHeader() {
  const { address: account, isConnected } = useAccount();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const location = useLocation();
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // Wagmi read contracts for token balances
  const { data: lionBalance, refetch: refetchLion } = useReadContract({
    address: TOKEN_ADDRESSES.LION,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: [account],
    chainId: KAVA_CHAIN_ID,
    query: {
      enabled: !!account && isConnected && chainId === KAVA_CHAIN_ID,
    },
  });

  const { data: tigerBalance, refetch: refetchTiger } = useReadContract({
    address: TOKEN_ADDRESSES.TIGER,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: [account],
    chainId: KAVA_CHAIN_ID,
    query: {
      enabled: !!account && isConnected && chainId === KAVA_CHAIN_ID,
    },
  });

  const { data: bearBalance, refetch: refetchBear } = useReadContract({
    address: TOKEN_ADDRESSES.BEAR,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: [account],
    chainId: KAVA_CHAIN_ID,
    query: {
      enabled: !!account && isConnected && chainId === KAVA_CHAIN_ID,
    },
  });

  // Format balances with thousand separators
  const balances = {
    LION: lionBalance ? Number(formatUnits(lionBalance, 18)).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '0',
    TIGER: tigerBalance ? Number(formatUnits(tigerBalance, 18)).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '0',
    BEAR: bearBalance ? Number(formatUnits(bearBalance, 18)).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '0',
  };

  // Prevent hydration mismatch
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Refetch balances periodically
  useEffect(() => {
    if (!account || !isConnected || chainId !== KAVA_CHAIN_ID) return;
    
    const interval = setInterval(() => {
      refetchLion();
      refetchTiger();
      refetchBear();
    }, 10000); // Refresh every 10s
    
    return () => clearInterval(interval);
  }, [account, isConnected, chainId, refetchLion, refetchTiger, refetchBear]);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Automatically switch to Kava when wallet is connected but on wrong chain
  useEffect(() => {
    if (isConnected && chainId !== KAVA_CHAIN_ID && switchChain) {
      console.log(`Wrong chain detected (${chainId}), switching to Kava (${KAVA_CHAIN_ID})`);
      
      // Attempt to switch chain
      switchChain(
        { chainId: KAVA_CHAIN_ID },
        {
          onSuccess: () => {
            console.log('Successfully switched to Kava network');
          },
          onError: (error) => {
            console.error('Failed to switch to Kava network:', error);
            alert('Please manually switch your wallet to the Kava network (Chain ID: 2222)');
          }
        }
      );
    }
  }, [isConnected, chainId, switchChain]);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: 'https://invest.scrub.money', label: 'Invest', external: true },
    { path: '/nftcollection', label: 'NFT' },
    { path: '/singlestake', label: 'Tiger Stake' },
    { path: 'https://commerce.scrub.money', label: 'Giftcards', external: true },
    { path: 'https://scrub.gitbook.io/scrub-doc/', label: 'Docs', external: true },
  ];

  return (
    <header className={`header-modern ${scrolled ? 'header-scrolled' : ''}`}>
      {/* Wrong Network Warning Banner */}
      {isMounted && isConnected && chainId !== KAVA_CHAIN_ID && (
        <div style={{
          backgroundColor: '#ff4444',
          color: 'white',
          padding: '12px 20px',
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px'
        }}>
          ⚠️ Wrong Network Detected - Please switch to Kava Network (Chain ID: 2222)
          {switchChain && (
            <button
              onClick={() => switchChain({ chainId: KAVA_CHAIN_ID })}
              style={{
                backgroundColor: 'white',
                color: '#ff4444',
                border: 'none',
                padding: '6px 16px',
                borderRadius: '6px',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '13px'
              }}
            >
              Switch to Kava
            </button>
          )}
        </div>
      )}
      
      <div className="header-container">
        {/* Logo */}
        <Link to="/" className="header-logo">
            <img src={tigerLogo} alt="Scrub Money" className="logo-img" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="header-nav desktop-nav">
            {navLinks.map((link) => 
              link.external ? (
                <a
                  key={link.label}
                  href={link.path}
                  className="nav-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          {/* Right Section - Desktop */}
          <div className="header-actions desktop-actions">
            {/* Kava Network Indicator */}
            <div className="network-indicator">
              <img src={kavaLogo} alt="Kava" className="network-icon" />
              <span className="network-name">Kava</span>
            </div>

            {/* Token Balances */}
            {isMounted && isConnected && account && (
              <div className="token-balances">
                <div className="balance-item">
                  <img src={lionLogo} alt="LION" className="token-icon" />
                  <span className="balance-amount">{Number(balances.LION).toLocaleString()}</span>
                </div>
                <div className="balance-item">
                  <img src={tigerLogo} alt="TIGER" className="token-icon" />
                  <span className="balance-amount">{Number(balances.TIGER).toLocaleString()}</span>
                </div>
                <div className="balance-item">
                  <img src={bearLogo} alt="BEAR" className="token-icon" />
                  <span className="balance-amount">{Number(balances.BEAR).toLocaleString()}</span>
                </div>
              </div>
            )}

            {/* ConnectKit Wallet Button */}
            {isMounted && (
              <ConnectKitButton.Custom>
                {({ isConnected, show, address, openAccountModal }) => {
                  return (
                    <button 
                      onClick={() => {
                        if (isConnected) {
                          // When connected, open account modal which has "Switch Wallet" option
                          openAccountModal?.();
                        } else {
                          // When not connected, show connect modal
                          show?.();
                        }
                      }} 
                      className="wallet-btn"
                    >
                      {isConnected ? (
                        <>
                          <div className="wallet-status-dot"></div>
                          <span className="wallet-text">
                            {address?.slice(0, 6)}...{address?.slice(-4)}
                          </span>
                        </>
                      ) : (
                        <span className="wallet-text">Connect Wallet</span>
                      )}
                    </button>
                  );
                }}
              </ConnectKitButton.Custom>
            )}

            {/* Social Links */}
            <div className="social-links">
              <a href="https://twitter.com/ScrubMoneyDeFi" target="_blank" rel="noopener noreferrer" className="social-link">
                <FaTwitter />
              </a>
              <a href="https://t.me/ScrubMoney" target="_blank" rel="noopener noreferrer" className="social-link">
                <FaTelegramPlane />
              </a>
              <a href="https://discord.gg/dPVjyrUBey" target="_blank" rel="noopener noreferrer" className="social-link">
                <BsDiscord />
              </a>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="mobile-menu open">
            <nav className="mobile-nav">
              {navLinks.map((link) => 
                link.external ? (
                  <a
                    key={link.label}
                    href={link.path}
                    className="mobile-nav-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`mobile-nav-link ${isActive(link.path) ? 'active' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                )
              )}
            </nav>

            {/* Mobile Actions */}
            <div className="mobile-actions">
              {/* Kava Network Indicator Mobile */}
              <div className="mobile-network-indicator">
                <img src={kavaLogo} alt="Kava" className="network-icon" />
                <span>Kava Network</span>
              </div>

              {/* Token Balances Mobile */}
              {isMounted && isConnected && account && (
                <div className="mobile-balances">
                  <div className="mobile-balance-item">
                    <img src={lionLogo} alt="LION" className="token-icon" />
                    <span>{Number(balances.LION).toLocaleString()} LION</span>
                  </div>
                  <div className="mobile-balance-item">
                    <img src={tigerLogo} alt="TIGER" className="token-icon" />
                    <span>{Number(balances.TIGER).toLocaleString()} TIGER</span>
                  </div>
                  <div className="mobile-balance-item">
                    <img src={bearLogo} alt="BEAR" className="token-icon" />
                    <span>{Number(balances.BEAR).toLocaleString()} BEAR</span>
                  </div>
                </div>
              )}

              {/* Wallet Button Mobile */}
              {isMounted && (
                <ConnectKitButton.Custom>
                  {({ isConnected, show, address, openAccountModal }) => {
                    return (
                      <button 
                        className="mobile-wallet-btn" 
                        onClick={() => {
                          if (isConnected) {
                            // When connected, open account modal which has "Switch Wallet" option
                            openAccountModal?.();
                          } else {
                            // When not connected, show connect modal
                            show?.();
                          }
                          setMobileMenuOpen(false);
                        }}
                      >
                        {isConnected ? (
                          <>
                            <div className="wallet-status-dot"></div>
                            <span>{address?.slice(0, 6)}...{address?.slice(-4)}</span>
                          </>
                        ) : (
                          <span>Connect Wallet</span>
                        )}
                      </button>
                    );
                  }}
                </ConnectKitButton.Custom>
              )}

              {/* Social Links Mobile */}
              <div className="mobile-social">
                <a href="https://twitter.com/ScrubMoneyDeFi" target="_blank" rel="noopener noreferrer" className="mobile-social-link">
                  <FaTwitter />
                </a>
                <a href="https://t.me/ScrubMoney" target="_blank" rel="noopener noreferrer" className="mobile-social-link">
                  <FaTelegramPlane />
                </a>
                <a href="https://discord.gg/dPVjyrUBey" target="_blank" rel="noopener noreferrer" className="mobile-social-link">
                  <BsDiscord />
                </a>
              </div>
            </div>
          </div>
        )}

      {/* Animated underline */}
      <div className="header-underline" />
    </header>
  );
}